import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { normalizeId } from '../utils/idNormalization';
import { transcriptKeys } from './queries';
import diffUtil from 'utils/DiffUtil';
import { buildParagraphs } from 'utils/ParagraphUtils';

/**
 * Hook to update transcript name
 * @param {string|number} projectId - The project ID (for invalidation)
 * @returns {Object} The mutation object
 */
export function useUpdateTranscriptName(projectId) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ transcriptId, name }) => {
      return QualAPI.updateTranscript(transcriptId, { name }).then(result => result.data);
    },
    
    onMutate: async ({ transcriptId, name }) => {
      const normalizedTranscriptId = normalizeId(transcriptId);
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: transcriptKeys.name(normalizedTranscriptId) 
      });
      
      // Snapshot the previous value
      const previousTranscript = queryClient.getQueryData(
        transcriptKeys.name(normalizedTranscriptId)
      );
      
      // Optimistically update the cache
      if (previousTranscript) {
        queryClient.setQueryData(
          transcriptKeys.name(normalizedTranscriptId),
          { ...previousTranscript, name }
        );
      }
      
      // Return a context object with the snapshotted value
      return { previousTranscript, transcriptId: normalizedTranscriptId };
    },
    
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTranscript) {
        queryClient.setQueryData(
          transcriptKeys.name(context.transcriptId),
          context.previousTranscript
        );
      }
    },
    
    onSuccess: (data, { transcriptId }) => {
      const normalizedTranscriptId = normalizeId(transcriptId);

      // Update the transcript name cache with the server response
      if (data) {
        queryClient.setQueryData(
          transcriptKeys.name(normalizedTranscriptId),
          data
        );
      }

      // Invalidate transcript list queries to refresh the transcripts list
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: transcriptKeys.lists()
        });
      }
    },
  });
}

/**
 * Hook to delete a transcript
 * @param {string|number} projectId - The project ID (for invalidation)
 * @returns {Object} The mutation object
 */
export function useDeleteTranscript(projectId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transcriptId) => {
      return QualAPI.deleteTranscript(transcriptId);
    },

    onError: () => {
      // Invalidate transcript list cache on error
      const normalizedProjectId = normalizeId(projectId);
      queryClient.invalidateQueries({
        queryKey: transcriptKeys.list(normalizedProjectId)
      });
    },

    onSuccess: (data, transcriptId) => {
      const normalizedTranscriptId = normalizeId(transcriptId);
      const normalizedProjectId = normalizeId(projectId);

      // Update the transcript list by removing the deleted transcript
      queryClient.setQueryData(
        transcriptKeys.list(normalizedProjectId),
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter(
            t => normalizeId(t.id) !== normalizedTranscriptId
          );
        }
      );

      // Invalidate the transcript count to ensure it's updated
      queryClient.invalidateQueries({
        queryKey: transcriptKeys.count(normalizedProjectId)
      });

      // Invalidate the Transcripts dashboard so the deleted row disappears
      queryClient.invalidateQueries({
        queryKey: transcriptKeys.dashboard(normalizedProjectId)
      });

      // Remove the transcript from cache
      queryClient.removeQueries({
        queryKey: transcriptKeys.detail(normalizedTranscriptId)
      });
      queryClient.removeQueries({
        queryKey: transcriptKeys.name(normalizedTranscriptId)
      });
    },
  });
}

/**
 * Hook to edit transcript paragraph text
 * @returns {Object} The mutation object
 */
export function useEditTranscriptMutation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ transcriptId, paragraphId, newText }) => {
      // Get the current transcript data from cache
      const transcriptData = queryClient.getQueryData(transcriptKeys.detail(transcriptId));
      if (!transcriptData) {
        throw new Error('Transcript data not found');
      }
      
      // Build paragraphs from transcript body
      const paragraphs = buildParagraphs(transcriptData.body);
      
      // Find the paragraph with matching index
      const paragraph = paragraphs.find(p => p.paragraphIndex === paragraphId);
      if (!paragraph) {
        throw new Error('Paragraph not found');
      }
      
      // Get original text and calculate diffs
      const originalText = transcriptData.body.substring(paragraph.start, paragraph.end);
      const diffs = diffUtil.diff(originalText, newText);
      
      // Create payload for API
      const payload = {
        version: transcriptData.version,
        delete: {
          start: paragraph.start,
          end: paragraph.end,
        },
        diffs: diffs,
      };
      
      return QualAPI.createTranscriptVersion(transcriptId, payload);
    },
    
    onSuccess: (data, variables) => {
      // Invalidate transcript detail to reload the updated transcript
      queryClient.invalidateQueries({ queryKey: transcriptKeys.detail(variables.transcriptId) });
    },
    
    onError: (error) => {
      // Version mismatch errors will have a specific error code
      if (error.response?.data?.error_code === 'VERSION_MISMATCH') {
        // The component will handle this with createErrorAlert
      }
      // Other errors will also be handled by the component
    },
  });
}
/**
 * Optimistically update the assignees of one transcript in the cached
 * dashboard data.
 */
function setDashboardAssignees(queryClient, projectId, transcriptId, updateAssignees) {
  const dashboardKey = transcriptKeys.dashboard(normalizeId(projectId));
  const previous = queryClient.getQueryData(dashboardKey);
  if (Array.isArray(previous)) {
    queryClient.setQueryData(dashboardKey, previous.map(transcript => {
      if (normalizeId(transcript.id) !== normalizeId(transcriptId)) return transcript;
      return { ...transcript, assignees: updateAssignees(transcript.assignees || []) };
    }));
  }
  return { previous, dashboardKey };
}

/**
 * Hook to assign a project member to a transcript (Transcripts dashboard)
 * @param {string|number} projectId - The project ID (for cache updates)
 * @returns {Object} The mutation object; mutate({ transcriptId, user }) where
 *   user is { id, name, email } from the project members list
 */
export function useAssignTranscript(projectId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transcriptId, user }) => {
      return QualAPI.assignTranscript(transcriptId, user.id).then(result => result.data);
    },

    onMutate: async ({ transcriptId, user }) => {
      await queryClient.cancelQueries({ queryKey: transcriptKeys.dashboards() });
      return setDashboardAssignees(queryClient, projectId, transcriptId, (assignees) => {
        if (assignees.some(a => String(a.user_id) === String(user.id))) return assignees;
        return [...assignees, { user_id: user.id, name: user.name, email: user.email }];
      });
    },

    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.dashboardKey, context.previous);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: transcriptKeys.dashboard(normalizeId(projectId))
      });
    },
  });
}

/**
 * Hook to unassign a project member from a transcript (Transcripts dashboard)
 * @param {string|number} projectId - The project ID (for cache updates)
 * @returns {Object} The mutation object; mutate({ transcriptId, userId })
 */
export function useUnassignTranscript(projectId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transcriptId, userId }) => {
      return QualAPI.unassignTranscript(transcriptId, userId).then(result => result.data);
    },

    onMutate: async ({ transcriptId, userId }) => {
      await queryClient.cancelQueries({ queryKey: transcriptKeys.dashboards() });
      return setDashboardAssignees(queryClient, projectId, transcriptId, (assignees) => {
        return assignees.filter(a => String(a.user_id) !== String(userId));
      });
    },

    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.dashboardKey, context.previous);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: transcriptKeys.dashboard(normalizeId(projectId))
      });
    },
  });
}
